import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Globe } from "lucide-react";

const navLinks = [
  { label: "How It Works", href: "/how-it-works" },
  { label: "AI Features", href: "/ai-features" },
  { label: "Teachers", href: "/teachers" },
  //{ label: "Events", href: "/events" },
  {
    label: "For Organizations",
    children: [
      { label: "Enterprises", href: "/enterprise", desc: "Team-building and global communication for companies" },
      { label: "Universities", href: "/universities", desc: "Language programs for students and faculty" },
      { label: "Governments", href: "/government", desc: "Multilingual solutions for public institutions" },
    ],
  },
  { label: "Pricing", href: "/pricing" },
  { label: "About", href: "/about" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActive = (href) => location.pathname === href;
  const onDark = isHome && !scrolled;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 ${
        scrolled
          ? "nav-glass border-b border-border shadow-sm-token"
          : "bg-transparent"
      }`}
      style={{ transition: "background 0.25s, box-shadow 0.25s" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group shrink-0">
            <div className="w-7 h-7 rounded-md bg-primary flex items-center justify-center">
              <Globe className="w-4 h-4 text-primary-foreground" strokeWidth={2.5} />
            </div>
            <span
              className={`text-lg font-display font-bold tracking-tight ${
                onDark ? "text-white" : "text-foreground"
              }`}
            >
              Lingua<span className="text-primary">2</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center">
            <NavigationMenu>
              <NavigationMenuList>
                {navLinks.map((link) =>
                  link.children ? (
                    <NavigationMenuItem key={link.label}>
                      <NavigationMenuTrigger
                        className={`text-sm font-medium bg-transparent hover:bg-transparent transition-fast ${
                          onDark
                            ? "text-white/80 hover:text-white data-[state=open]:text-white"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {link.label}
                      </NavigationMenuTrigger>
                      <NavigationMenuContent>
                        <ul className="grid w-[360px] gap-1 p-3">
                          {link.children.map((child) => (
                            <li key={child.label}>
                              <NavigationMenuLink asChild>
                                <Link
                                  to={child.href}
                                  className="block rounded-md p-3 hover:bg-secondary group transition-fast"
                                >
                                  <div className="text-sm font-semibold text-foreground group-hover:text-primary mb-0.5 transition-fast">
                                    {child.label}
                                  </div>
                                  <div className="text-xs text-muted-foreground">{child.desc}</div>
                                </Link>
                              </NavigationMenuLink>
                            </li>
                          ))}
                        </ul>
                      </NavigationMenuContent>
                    </NavigationMenuItem>
                  ) : (
                    <NavigationMenuItem key={link.label}>
                      <NavigationMenuLink asChild>
                        <Link
                          to={link.href}
                          className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-fast ${
                            isActive(link.href)
                              ? onDark
                                ? "text-white bg-white/15"
                                : "text-primary bg-primary/8"
                              : onDark
                              ? "text-white/80 hover:text-white hover:bg-white/10"
                              : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                          }`}
                        >
                          {link.label}
                        </Link>
                      </NavigationMenuLink>
                    </NavigationMenuItem>
                  )
                )}
              </NavigationMenuList>
            </NavigationMenu>
          </nav>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              asChild
              className={`text-sm transition-fast ${
                onDark
                  ? "text-white/80 hover:text-white hover:bg-white/10"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              
            </Button>
            <Button
              size="sm"
              asChild
              className="bg-primary hover:bg-primary-dark text-primary-foreground font-semibold shadow-primary transition-fast"
            >
              <Link to="/contact"> Contact Sales</Link>
            </Button>
          </div>

          {/* Mobile */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={`lg:hidden ${onDark ? "text-white hover:bg-white/10" : "text-foreground"}`}
              >
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] pt-8">
              <div className="flex items-center gap-2 mb-8">
                <div className="w-7 h-7 rounded-md bg-primary flex items-center justify-center">
                  <Globe className="w-4 h-4 text-primary-foreground" strokeWidth={2.5} />
                </div>
                <span className="text-lg font-display font-bold">
                  Lingua<span className="text-primary">2</span>
                </span>
              </div>
              <nav className="flex flex-col gap-1">
                {navLinks.map((link) =>
                  link.children ? (
                    <div key={link.label}>
                      <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider mt-3">
                        {link.label}
                      </div>
                      {link.children.map((child) => (
                        <Link
                          key={child.label}
                          to={child.href}
                          onClick={() => setMobileOpen(false)}
                          className={`block px-5 py-2 text-sm rounded-md transition-fast ${
                            isActive(child.href)
                              ? "text-primary bg-primary/8 font-medium"
                              : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                          }`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <Link
                      key={link.label}
                      to={link.href}
                      onClick={() => setMobileOpen(false)}
                      className={`block px-3 py-2 text-sm rounded-md transition-fast ${
                        isActive(link.href)
                          ? "text-primary bg-primary/8 font-medium"
                          : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                      }`}
                    >
                      {link.label}
                    </Link>
                  )
                )}
                <div className="border-t border-border mt-4 pt-4 flex flex-col gap-2">
                  <Button variant="outline" size="sm" asChild className="w-full">
                    
                  </Button>
                  <Button
                    size="sm"
                    asChild
                    className="w-full bg-primary hover:bg-primary-dark text-primary-foreground font-semibold"
                  >
                    <Link to="/contact" onClick={() => setMobileOpen(false)}>Request Demo</Link>
                  </Button>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
